/**
 * Focus containment for the Globo Graphics drawers.
 *
 * These panels were `v-dialog` before the redesign. VDialog traps focus; the
 * VNavigationDrawer they became does not — it has no keydown handling at all. So Tab
 * walked straight out of an open drawer and into the page behind it. The controls back
 * there are not *clickable* (VNavigationDrawer mixes in Overlayable, which renders a
 * real overlay that swallows pointer events), which is exactly why this was easy to
 * miss: the drawer looks modal and behaves modally for a mouse, while a keyboard walks
 * out of it into a page it cannot see.
 *
 * Kept as plain functions rather than folded into the mixin because one of the five
 * drawers — the Graphic Hub browse panel in GloboGraphicsCenasDisponiveis — is NOT its
 * host component's root element and owns its open state via v-model, so it cannot use
 * the mixin. Two copies of a focus trap is precisely the kind of drift that put the
 * scoping bugs in this codebase, so both call the same code.
 */

// Deliberately excludes [tabindex="-1"]: programmatically focusable, not tab-reachable.
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Tab-reachable elements inside `root`, in document order.
 *
 * `offsetParent === null` filters out anything hidden by display:none or a collapsed
 * ancestor — a drawer body typically holds a search field and a list that only exists
 * once loaded, and tabbing into a hidden control looks to the user like focus vanished.
 * position:fixed elements report a null offsetParent too, hence the rect fallback.
 */
export function focusableWithin(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el.getBoundingClientRect().width > 0
    );
}

/**
 * Keeps Tab and Shift+Tab inside `root`. Call from a keydown listener.
 * Returns true when it handled the event.
 */
export function trapTab(root, event) {
    if (event.key !== 'Tab' || !root) return false;

    const items = focusableWithin(root);
    if (!items.length) {
        // An empty drawer (still loading, or an empty state with no controls) would
        // otherwise let Tab escape. Nothing to focus, so just refuse to move.
        event.preventDefault();
        return true;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    // Focus is outside the drawer entirely — it started on the page behind, or the
    // element holding it was just re-rendered away. Pull it back to the top.
    if (!root.contains(active)) {
        event.preventDefault();
        first.focus();
        return true;
    }

    if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
        return true;
    }
    if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
        return true;
    }
    return false;
}

/**
 * Moves focus to the first control in `root`, falling back to `root` itself so a
 * screen reader still lands inside the dialog when it has no focusable content yet.
 * Returns the element that had focus beforehand, for restoreFocus().
 */
export function focusFirstWithin(root) {
    const previous = document.activeElement;
    if (!root) return previous;

    const items = focusableWithin(root);
    if (items.length) {
        items[0].focus();
    } else if (typeof root.focus === 'function') {
        // -1 so it is focusable programmatically without joining the tab order.
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
        root.focus();
    }
    return previous;
}

/**
 * Returns focus to whatever held it before the drawer opened. Guarded because the
 * trigger can be unmounted while the drawer is open (opening a drawer from a row that
 * a refresh then replaces), in which case the node is no longer in the document and
 * focusing it would silently drop focus to <body>.
 */
export function restoreFocus(previous) {
    if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus();
    }
}
